import { makeAutoObservable } from 'mobx'

import { setSettingForApp } from 'entities/user/module/app-settings'
import userStore from 'entities/user/module/user-store'

import { GuestsLimits } from '../../config/guests'
import type { IRange } from '../../lib/use-calendar-kyrgyzstan'
import type { GuestsState } from '../guests'

import type { DetailSearchParams } from './detail-data-store'

const KYRGYZSTAN_DETAIL_CONTEXT_STORAGE_KEY = 'kyrgyzstan_new_detail_context'

export interface KyrgyzstanDetailContext {
  parentId: string | null
  detailSearchParams: DetailSearchParams | null
}

export interface SetKyrgyzstanDetailContextParams {
  parentId: string
  detailSearchParams?: DetailSearchParams | null
}

class KyrgyzstanDetailContextStore {
  parentId: string | null = null

  detailSearchParams: DetailSearchParams | null = null

  constructor() {
    makeAutoObservable(this)
  }

  setContext = ({
    parentId,
    detailSearchParams = null,
  }: SetKyrgyzstanDetailContextParams) => {
    void setSettingForApp({
      key: KYRGYZSTAN_DETAIL_CONTEXT_STORAGE_KEY,
      value: '',
    })

    this.parentId = parentId
    this.detailSearchParams = detailSearchParams
  }

  setDetailSearchParams = (detailSearchParams: DetailSearchParams | null) => {
    void setSettingForApp({
      key: KYRGYZSTAN_DETAIL_CONTEXT_STORAGE_KEY,
      value: '',
    })

    this.detailSearchParams = detailSearchParams
  }

  setBookingGuests = (guests: GuestsState) => {
    const currentParams = { ...(this.detailSearchParams ?? {}) }
    const normalizedChildrenAges =
      guests.children > 0
        ? Array.from(
            { length: guests.children },
            (_, index) =>
              guests.childrenAges[index] ?? GuestsLimits.childAge.default,
          )
        : []

    delete currentParams.children_ages

    this.detailSearchParams = {
      ...currentParams,
      visitors_count: guests.adults,
      ...(normalizedChildrenAges.length
        ? { children_ages: normalizedChildrenAges }
        : {}),
    }
  }

  setBookingDates = (dates: IRange) => {
    const currentParams = { ...(this.detailSearchParams ?? {}) }
    delete currentParams.start_date
    delete currentParams.end_date

    this.detailSearchParams = {
      ...currentParams,
      ...(dates.startDate ? { start_date: dates.startDate } : {}),
      ...(dates.endDate ? { end_date: dates.endDate } : {}),
    }
  }

  prepareContextForParent = (parentId: string) => {
    if (this.parentId === parentId) return

    this.clearContext()
    this.parentId = parentId
  }

  restoreSavedContext = () => {
    const savedContext = userStore.getClientStateParse<KyrgyzstanDetailContext>(
      KYRGYZSTAN_DETAIL_CONTEXT_STORAGE_KEY,
    )

    if (!savedContext) return

    this.parentId = savedContext.parentId
    this.detailSearchParams = savedContext.detailSearchParams
    void setSettingForApp({
      key: KYRGYZSTAN_DETAIL_CONTEXT_STORAGE_KEY,
      value: '',
    })
  }

  saveContext = () => {
    if (!this.parentId) return

    const context: KyrgyzstanDetailContext = {
      parentId: this.parentId,
      detailSearchParams: this.detailSearchParams,
    }

    void setSettingForApp({
      key: KYRGYZSTAN_DETAIL_CONTEXT_STORAGE_KEY,
      value: JSON.stringify(context),
    })
  }

  clearContext = () => {
    this.parentId = null
    this.detailSearchParams = null

    void setSettingForApp({
      key: KYRGYZSTAN_DETAIL_CONTEXT_STORAGE_KEY,
      value: '',
    })
  }
}

export const kyrgyzstanDetailContextStore = new KyrgyzstanDetailContextStore()
